import React, { useMemo } from 'react';
import { WORLD } from '../config/zones';
import { mulberry32 } from './rng';
import { shared } from './shared';
import { useStore } from '../store';
import InstancedSpecies, { ARTIFACTS } from './InstancedSpecies';
import { SPECIES } from './species';
import { LOW_POWER } from './shared';

export { ARTIFACTS };

// Land, predator and canopy fauna — every species is fully InstancedMesh-backed.
// `wave` streams species in for a smooth first load: 0 = birds/canopy, 1 = + ground
// herds, 2 = + predators/basking. Weaker devices (LOW_POWER) start with fewer animals.
export default function Wildlife({ wave = 2 }) {
  useMemo(() => {
    shared.herds.elephants = [];
    shared.herds.donkeys = [];
    shared.predators = [];
    shared.fauna = [];
  }, []);

  const ws = useStore.getState().worldSeed;
  const r = mulberry32(ws + 7);
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const light = (params && (params.get('photo') || params.get('lowfx') === '1')) || LOW_POWER;
  const q = light ? 0.4 : 1;
  const c = (base, span) => Math.max(2, Math.round((base + Math.floor(r() * span)) * q));

  const leftBank = { xMin: WORLD.xMin + 20, xMax: WORLD.xMax - 20, zMin: -95, zMax: -30, y: 0 };
  const rightBank = { xMin: WORLD.xMin + 20, xMax: WORLD.xMax - 20, zMin: 30, zMax: 95, y: 0 };
  const roam = { xMin: WORLD.xMin + 30, xMax: WORLD.xMax - 30, zMin: -28, zMax: 28, y: 0 };
  const interior = { xMin: WORLD.xMin + 45, xMax: WORLD.xMax - 45, zMin: -46, zMax: 46, y: 0 };
  const riverbank = { xMin: WORLD.xMin + 25, xMax: WORLD.xMax - 25, zMin: -20, zMax: 20, y: -0.05 };
  const canopyWide = { xMin: WORLD.xMin + 20, xMax: WORLD.xMax - 20, zMin: -90, zMax: 90, y: 0 };
  const canopyLeft = { xMin: WORLD.xMin + 20, xMax: WORLD.xMax - 20, zMin: -90, zMax: -30, y: 11 };
  const canopyBoth = { xMin: WORLD.xMin + 20, xMax: WORLD.xMax - 20, zMin: -90, zMax: 90, y: 8 };
  const canopyHigh = { xMin: WORLD.xMin + 20, xMax: WORLD.xMax - 20, zMin: -90, zMax: 90, y: 12 };

  return (
    <group>
      {/* Wave 0 — canopy & birds fill the trees/sky first */}
      <InstancedSpecies parts={SPECIES.macaw} count={c(16, 10)} bounds={canopyWide} seed={8100 + ws} behavior="canopyfly" speciesKey="macaw" />
      <InstancedSpecies parts={SPECIES.sloth} count={c(6, 5)} bounds={canopyLeft} seed={8200 + ws} behavior="perch" speciesKey="sloth" />
      <InstancedSpecies parts={SPECIES.anaconda} count={c(6, 4)} bounds={canopyBoth} seed={8300 + ws} behavior="perch" speciesKey="anaconda" />
      <InstancedSpecies parts={SPECIES.howler} count={c(8, 6)} bounds={canopyHigh} seed={8400 + ws} behavior="perch" pop speciesKey="howler" />

      {/* Wave 1 — ground herds graze in next */}
      {wave >= 1 && (
        <>
          <InstancedSpecies parts={SPECIES.donkey} count={c(10, 6)} bounds={leftBank} seed={11 + ws} speciesKey="donkey" flee registerTo={shared.herds.donkeys} />
          <InstancedSpecies parts={SPECIES.elephant} count={c(7, 5)} bounds={rightBank} seed={2200 + ws} speciesKey="elephant" behavior="guardWall" registerTo={shared.herds.elephants} />
        </>
      )}

      {/* Wave 2 — apex predators & basking caimans arrive last */}
      {wave >= 2 && (
        <>
          <InstancedSpecies parts={SPECIES.jaguar} count={c(5, 4)} bounds={roam} seed={5000 + ws} behavior="stalk" speciesKey="jaguar" registerTo={shared.predators} />
          <InstancedSpecies parts={SPECIES.lion} count={c(4, 3)} bounds={interior} seed={6100 + ws} behavior="hunt" speciesKey="lion" registerTo={shared.predators} />
          <InstancedSpecies parts={SPECIES.tiger} count={c(4, 3)} bounds={interior} seed={6200 + ws} behavior="hunt" speciesKey="tiger" registerTo={shared.predators} />
          <InstancedSpecies parts={SPECIES.bear} count={c(4, 3)} bounds={interior} seed={6300 + ws} behavior="wander" speciesKey="bear" />
          <InstancedSpecies parts={SPECIES.caiman} count={c(6, 4)} bounds={riverbank} seed={7100 + ws} behavior="wander" speciesKey="caiman" />
        </>
      )}
    </group>
  );
}
